import { FormControl, FormGroup } from '@angular/forms';
import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  inject,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { LoggerService } from '@services/logger.service';
import { FormsService } from '../../services/forms.service';
import { distinctUntilChanged, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-object-permissions-simple',
  templateUrl: './object-permissions-simple.component.html',
  styleUrls: ['./object-permissions-simple.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ObjectPermissionsSimpleComponent implements OnInit, OnChanges {
  // now plain inputs (provided by wrapper)
  @Input() formGroup: FormGroup;
  @Input() principals: any[] = [];
  @Input() permissions: any[] = [];
  @Input() NomObjectPermission: any; // enum
  @Input() permissionRoleList: Array<{ id: number; value: string }> = [];

  // outputs to wrapper
  @Output() removePrincipalRequested = new EventEmitter<any>();
  @Output() addPrincipalsRequested = new EventEmitter<void>();
  @Output() principalRoleChange = new EventEmitter<{
    controlKey: string;
    roleId: number;
  }>();

  protected destroyRef = inject(DestroyRef);

  constructor(
    protected formService: FormsService,
    protected logger: LoggerService,
  ) {}

  ngOnInit(): void {
    // controls are created when principals arrive
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['principals']) {
      this.rebuildPrincipalControls();
    }
  }

  private rebuildPrincipalControls() {
    // cleanup existing controls that match the pattern
    Object.keys(this.formGroup.controls)
      .filter((k) => k.startsWith('permission['))
      .forEach((k) => this.formGroup.removeControl(k));

    this.principals?.forEach((principal) => {
      const key = `permission[${principal.object_class_id}][${principal.id}]`;
      const disabled =
        principal.permission_role_id === this.NomObjectPermission?.OWNER ||
        principal.permission_role_id === this.NomObjectPermission?.ADMIN;
      // const disabled = false;
      const ctrl = new FormControl({
        value: principal.permission_role_id,
        disabled,
      });

      // listen for changes and bubble up
      ctrl.valueChanges
        .pipe(
          // only emit when the roleId actually changes
          distinctUntilChanged(),
          // side-effect: emit the role change
          tap((roleId) => {
            this.principalRoleChange.emit({ controlKey: key, roleId });
          }),
          // auto-unsubscribe on component destroy
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();

      this.formGroup.addControl(key, ctrl);
    });
  }

  // UI actions -> emit to wrapper
  removePrincipal(principal: any) {
    this.formGroup.removeControl(
      `permission[${principal.object_class_id}][${principal.id}]`,
    );
    this.removePrincipalRequested.emit(principal);
  }

  addPrincipals(): void {
    this.addPrincipalsRequested.emit();
  }
}
